import { type IValidator } from './';

export class TimeValidator implements IValidator {
    status: string = '';
    statusType: 'info' | 'warning' | 'error' | 'na' = 'error';
    value?: string;
    format: string = 'h:m:s';
    from: Date;
    to: Date;
    hourIndex: number | null = null;
    minuteIndex: number | null = null;
    secondIndex: number | null = null;
    millisecondsIndex: number | null = null;
    yearIndex: number | null = null;
    monthIndex: number | null = null;
    dayIndex: number | null = null;
    hasDateInFormat: boolean = false;

    public constructor(format: string, from: Date, to: Date) {
        this.setFormat(format);
        this.from = from;
        this.to = to;
    }

    Status(): string {
        return this.status;
    }

    StatusType(): 'info' | 'warning' | 'error' | 'na' {
        return this.statusType;
    }

    Value(): string {
        return this.value as string;
    }

    private setFormat(value: string): void {
        this.hourIndex = null;
        this.minuteIndex = null;
        this.secondIndex = null;
        this.millisecondsIndex = null;
        this.yearIndex = null;
        this.monthIndex = null;
        this.dayIndex = null;
        this.hasDateInFormat = false;

        const formatParts = value.toLowerCase().match(/[a-z]+|[^a-z]+/g);
        if (formatParts == null) {
            this.format = value;
            return;
        }

        let splitChar = '';
        let valueIndex = 0;

        formatParts.forEach((part: string) => {
            if (/^[a-z]+$/.test(part)) {
                const token = part[0];

                if (token === 'm') {
                    // m means month for dot-separated date parts and minute for time parts.
                    if (splitChar.includes('.')) {
                        this.monthIndex = valueIndex;
                    } else {
                        this.minuteIndex = valueIndex;
                    }
                } else if (token === 'd') {
                    this.dayIndex = valueIndex;
                } else if (token === 'y') {
                    this.yearIndex = valueIndex;
                } else if (token === 'h') {
                    this.hourIndex = valueIndex;
                } else if (token === 's') {
                    this.secondIndex = valueIndex;
                } else if (token === 'f') {
                    this.millisecondsIndex = valueIndex;
                }

                valueIndex++;
                return;
            }

            splitChar = part;
        });

        this.hasDateInFormat = this.dayIndex != null || this.monthIndex != null || this.yearIndex != null;

        this.format = value;
    }

    /**
     * Validatae the cell value as a date
     * @param cellvalue
     * @returns boolean
     */
    Validate(cellvalue: string): boolean {
        if (this.format === '') {
            this.status = 'timevalidator.noformat';
            this.statusType = 'warning';
            return false;
        }

        const _value: number[] = cellvalue
            .replace(/\D/g, ':')
            .split(':')
            .filter((i) => i)
            .map((i) => parseInt(i));

        if (this.hourIndex == null || this.minuteIndex == null) {
            this.status = 'timevalidator.noformat';
            this.statusType = 'error';
            return false;
        }

        const hasFullDateInFormat = this.dayIndex != null && this.monthIndex != null && this.yearIndex != null;
        if (this.hasDateInFormat && !hasFullDateInFormat) {
            this.status = 'timevalidator.noformat';
            this.statusType = 'error';
            return false;
        }

        const hasSecondsInFormat = this.secondIndex != null;
        const expectedValues = 2 + (hasSecondsInFormat ? 1 : 0) + (this.millisecondsIndex != null ? 1 : 0) + (hasFullDateInFormat ? 3 : 0);
        const minExpectedValues = expectedValues - (hasSecondsInFormat ? 1 : 0);

        if (_value.length !== expectedValues && _value.length !== minExpectedValues) {
            this.status = 'timevalidator.noformat';
            this.statusType = 'error';
            return false;
        }

        const parsedValues = [..._value];
        if (hasSecondsInFormat && this.secondIndex != null && _value.length === minExpectedValues) {
            // When seconds are omitted, insert 0 so subsequent indexes remain aligned.
            parsedValues.splice(this.secondIndex, 0, 0);
        }

        if (_value.length == 4 && this.millisecondsIndex == null) {
            this.status = 'timevalidator.ignoremillisecond';
            this.statusType = 'warning';
            return false;
        }

        if (parsedValues[this.hourIndex] < 0 || parsedValues[this.hourIndex] > 23) {
            this.status = 'timevalidator.invalidhour';
            this.statusType = 'error';
            return false;
        }

        if (parsedValues[this.minuteIndex] < 0 || parsedValues[this.minuteIndex] > 59) {
            this.status = 'timevalidator.invalidminute';
            this.statusType = 'error';
            return false;
        }

        if (this.secondIndex != null && (parsedValues[this.secondIndex] < 0 || parsedValues[this.secondIndex] > 59)) {
            this.status = 'timevalidator.invalidsecond';
            this.statusType = 'error';
            return false;
        }

        if (this.millisecondsIndex != null && (parsedValues[this.millisecondsIndex] < 0 || parsedValues[this.millisecondsIndex] > 999)) {
            this.status = 'timevalidator.invalidmillisecond';
            this.statusType = 'error';
            return false;
        }

        if (hasFullDateInFormat && this.yearIndex != null && this.monthIndex != null && this.dayIndex != null) {
            const inputYear = parsedValues[this.yearIndex];
            const inputMonth = parsedValues[this.monthIndex];
            const inputDay = parsedValues[this.dayIndex];

            const currentYear: number = new Date().getFullYear() - 2000;
            let normalizedYear = inputYear;

            if (normalizedYear < currentYear) {
                normalizedYear += 2000;
            } else if (normalizedYear < 100) {
                normalizedYear += 1900;
            }

            if (normalizedYear < 1899 || normalizedYear > currentYear + 2100) {
                this.status = 'timevalidator.invaliddate';
                this.statusType = 'error';
                return false;
            }

            if (inputMonth < 1 || inputMonth > 12 || inputDay < 1 || inputDay > 31) {
                this.status = 'timevalidator.invaliddate';
                this.statusType = 'error';
                return false;
            }

            // Check if the time falls within the specified range
            const inputDate = new Date();
            inputDate.setFullYear(normalizedYear, inputMonth - 1, inputDay);
            inputDate.setHours(parsedValues[this.hourIndex] || 0);
            inputDate.setMinutes(parsedValues[this.minuteIndex] || 0);
            inputDate.setSeconds(this.secondIndex != null ? parsedValues[this.secondIndex] : 0);
            inputDate.setMilliseconds(this.millisecondsIndex != null ? parsedValues[this.millisecondsIndex] : 0);

            if (inputDate < this.from || inputDate > this.to) {
                this.status = 'timevalidator.outofrange';
                this.statusType = 'error';
                return false;
            }
        }

        return true;
    }
}
